/**
 * Danish presentation formatters for every portal. Ported from public-ui-lib's `format.util.ts`
 * (outputs must stay identical), plus `formatDkDate`, which the Angular apps did with their date
 * pipe.
 *
 * Always `da-DK`, and dates always `dd-MM-yyyy` (CLAUDE.md): the Angular portals render some
 * prices through the `number` pipe with no locale registered, i.e. en-US grouping ("1,234"), and
 * `toLocaleDateString('da-DK')` gives "18.6.2026". Both are bugs not to carry over.
 */

/** Money, Danish-grouped with two decimals and a unit, e.g. 1234.5 -> "1.234,50 kr." */
export function formatKr(value: number): string {
  return (
    value.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.'
  );
}

/** Tread depth in millimetres, at most one decimal, e.g. 7.25 -> "7,3" */
export function formatMm(value: number): string {
  return value.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
}

/** Danish-grouped whole number, e.g. 15000 -> "15.000". Empty for null. */
export function formatKm(value: number | null | undefined): string {
  return value == null ? '' : value.toLocaleString('da-DK', { maximumFractionDigits: 0 });
}

/** A whole Danish-grouped number, rounded half-up, e.g. 1234.5 -> "1.235"; empty for null. */
export function formatWhole(value: number | null | undefined): string {
  return value == null
    ? ''
    : Math.round(value).toLocaleString('da-DK', { maximumFractionDigits: 0 });
}

/** Rounded UP to a whole number (monthly prices a customer pays), e.g. 505.12 -> "506". */
export function formatWholeUp(value: number): string {
  return Math.ceil(value).toLocaleString('da-DK', { maximumFractionDigits: 0 });
}

/** Two decimals, Danish, no unit, e.g. 99.5 -> "99,50". */
export function formatDecimal2(value: number): string {
  return value.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * A user-visible date: Danish `dd-MM-yyyy` (e.g. "18-06-2026"), per CLAUDE.md. Empty for a
 * missing or unparseable value.
 *
 * Read in local time, like Angular's date pipe it replaces: API dates arrive as UTC-midnight ISO
 * strings, which in Danish time (UTC+1/+2) fall on the same calendar day.
 *
 * Use this for PDF preview variables too — the Angular flows used `toLocaleDateString('da-DK')`
 * there, which gives "18.6.2026".
 */
export function formatDkDate(value: Date | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${date.getFullYear()}`;
}

/** A month for display: `MM-yyyy` (the Danish order, as the schedule shows it). Empty for null. */
export function formatDkMonth(value: Date | null | undefined): string {
  if (!value || Number.isNaN(value.getTime())) {
    return '';
  }
  return `${String(value.getMonth() + 1).padStart(2, '0')}-${value.getFullYear()}`;
}
